import { useState, useMemo, useEffect } from 'react';
import { Box, Paper, Typography, Menu, MenuItem, Button } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getStigSummary, getLibraryStatus } from '../api/stig';
import StigDetailDrawer from '../components/StigDetailDrawer';
import BulkStigAssignDialog from '../components/BulkStigAssignDialog';
import BulkCklUploadDialog from '../components/BulkCklUploadDialog';

// ── ColFilter ──────────────────────────────────────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }} sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

// ── Stat Box ───────────────────────────────────────────────────

function StatBox({ label, value, color }) {
  return (
    <Paper sx={{ flex: 1, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px 20px', textAlign: 'center' }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

// ── Main Page ──────────────────────────────────────────────────

export default function StigCompliance({ onSelectDevice, onSelectGroup }) {
  const [devices, setDevices] = useState([]);
  const [filterOS, setFilterOS] = useState([]);
  const [filterType, setFilterType] = useState([]);
  const [filterCatI, setFilterCatI] = useState([]);
  const [filterCatII, setFilterCatII] = useState([]);
  const [filterCatIII, setFilterCatIII] = useState([]);
  const [filterOpen, setFilterOpen] = useState([]);
  const [filterNAF, setFilterNAF] = useState([]);
  const [filterNA, setFilterNA] = useState([]);
  const [filterNR, setFilterNR] = useState([]);

  const [libraryStatus, setLibraryStatus] = useState(null);
  const [drawerAsset, setDrawerAsset] = useState(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkCklOpen, setBulkCklOpen] = useState(false);

  useEffect(() => {
    getStigSummary().then(setDevices).catch(console.error);
    getLibraryStatus().then(setLibraryStatus).catch(console.error);
  }, []);

  const stats = useMemo(() => {
    if (!devices.length) return { open: 0, notAFinding: 0, notApplicable: 0, notReviewed: 0, catI: 0 };
    return {
      open:         devices.reduce((s, d) => s + (d.open || 0), 0),
      notAFinding:  devices.reduce((s, d) => s + (d.notAFinding || 0), 0),
      notApplicable:devices.reduce((s, d) => s + (d.notApplicable || 0), 0),
      notReviewed:  devices.reduce((s, d) => s + (d.notReviewed || 0), 0),
      catI:         devices.reduce((s, d) => s + d.catI, 0),
    };
  }, [devices]);

  const osOptions = useMemo(() => Array.from(new Set(devices.map(d => d.os))).sort(), [devices]);

  const filtered = useMemo(() => devices.filter(d => {
    if (filterOS.length > 0 && !filterOS.includes(d.os)) return false;
    if (filterType.length > 0 && !filterType.includes(d.type)) return false;
    const catCheck = (f, val) => {
      if (f.length === 0) return true;
      const has = f.includes('Has Findings') && val > 0;
      const none = f.includes('None') && val === 0;
      return has || none;
    };
    if (!catCheck(filterCatI,   d.catI))          return false;
    if (!catCheck(filterCatII,  d.catII))          return false;
    if (!catCheck(filterCatIII, d.catIII))         return false;
    if (!catCheck(filterOpen,   d.open))           return false;
    if (!catCheck(filterNAF,    d.notAFinding))    return false;
    if (!catCheck(filterNA,     d.notApplicable))  return false;
    if (!catCheck(filterNR,     d.notReviewed))    return false;
    return true;
  }), [devices, filterOS, filterType, filterCatI, filterCatII, filterCatIII, filterOpen, filterNAF, filterNA, filterNR]);

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Library Status Bar */}
      {libraryStatus && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2, padding: '8px 14px', backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2 }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>STIG Library</Typography>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: libraryStatus.up_to_date ? '#4caf50' : '#ff9800' }} />
          <Typography sx={{ color: '#ffffff', fontSize: 12, fontWeight: 600 }}>
            {libraryStatus.installed.label}
          </Typography>
          {libraryStatus.update_available ? (
            <>
              <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>→ Update available:</Typography>
              <Typography sx={{ color: '#ff9800', fontSize: 12, fontWeight: 600 }}>{libraryStatus.latest.label}</Typography>
              <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>({libraryStatus.latest.size_mb} MB)</Typography>
            </>
          ) : (
            <Typography sx={{ color: '#4caf50', fontSize: 11 }}>· Up to date</Typography>
          )}
          <Typography sx={{ color: '#4a5a70', fontSize: 11, marginLeft: 'auto' }}>
            Source: dl.dod.cyber.mil · checked on load
          </Typography>
          <Button
            onClick={() => setBulkCklOpen(true)}
            variant="outlined"
            size="small"
            sx={{ borderColor: '#7a8fa6', color: '#7a8fa6', fontSize: 11, '&:hover': { backgroundColor: '#7a8fa611' } }}
          >
            Upload CKL
          </Button>
          <Button
            onClick={() => setBulkAssignOpen(true)}
            variant="outlined"
            size="small"
            sx={{ borderColor: '#00bcd4', color: '#00bcd4', fontSize: 11, '&:hover': { backgroundColor: '#00bcd411' } }}
          >
            Bulk Assign STIG
          </Button>
        </Box>
      )}

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        <StatBox label="Open"            value={stats.open}          color={stats.open > 0 ? '#f44336' : '#4caf50'} />
        <StatBox label="Not a Finding"   value={stats.notAFinding}   color="#4caf50" />
        <StatBox label="Not Applicable"  value={stats.notApplicable} color="#00bcd4" />
        <StatBox label="Not Reviewed"    value={stats.notReviewed}   color="#7a8fa6" />
        <StatBox label="Open CAT I"      value={stats.catI}          color={stats.catI > 0 ? '#f44336' : '#4caf50'} />
      </Box>

      {/* Table */}
      <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr 60px 60px 70px 80px 110px 110px 110px', padding: '10px 16px', borderBottom: '1px solid #1e3a5f', backgroundColor: '#0d1825' }}>
          <ColFilter label="Device"          options={[]}                         value={[]}         onChange={() => {}} />
          <ColFilter label="OS"              options={osOptions}                  value={filterOS}   onChange={setFilterOS} />
          <ColFilter label="CAT I"           options={['Has Findings', 'None']}   value={filterCatI}   onChange={setFilterCatI} />
          <ColFilter label="CAT II"          options={['Has Findings', 'None']}   value={filterCatII}  onChange={setFilterCatII} />
          <ColFilter label="CAT III"         options={['Has Findings', 'None']}   value={filterCatIII} onChange={setFilterCatIII} />
          <ColFilter label="Open"            options={['Has Findings', 'None']}   value={filterOpen}   onChange={setFilterOpen} />
          <ColFilter label="Not a Finding"   options={['Has Findings', 'None']}   value={filterNAF}    onChange={setFilterNAF} />
          <ColFilter label="Not Applicable"  options={['Has Findings', 'None']}   value={filterNA}     onChange={setFilterNA} />
          <ColFilter label="Not Reviewed"    options={['Has Findings', 'None']}   value={filterNR}     onChange={setFilterNR} />
        </Box>

        {/* Rows */}
        {filtered.map((d, i) => (
          <Box key={d.hostname} sx={{
            display: 'grid',
            gridTemplateColumns: '180px 1fr 60px 60px 70px 80px 110px 110px 110px',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: i < filtered.length - 1 ? '1px solid #0d1825' : 'none',
            '&:hover': { backgroundColor: '#0d1f33' },
            cursor: 'default',
          }}>
            <Typography
              onClick={() => setDrawerAsset({ id: d.id, hostname: d.hostname, ip: d.ip_address, os: d.os })}
              sx={{ color: '#ffffff', fontWeight: 600, fontFamily: 'monospace', fontSize: 13, cursor: 'pointer', '&:hover': { color: '#00bcd4', textDecoration: 'underline' } }}
            >{d.hostname}</Typography>
            <Typography
              onClick={() => onSelectGroup?.({ id: 'os-' + d.os, label: d.os, type: 'os', value: d.os })}
              sx={{ color: '#7a8fa6', fontSize: 12, cursor: onSelectGroup ? 'pointer' : 'default', '&:hover': onSelectGroup ? { color: '#00bcd4', textDecoration: 'underline' } : {} }}
            >{d.os}</Typography>
            <Typography sx={{ color: d.catI > 0 ? '#f44336' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.catI}</Typography>
            <Typography sx={{ color: d.catII > 0 ? '#ff9800' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.catII}</Typography>
            <Typography sx={{ color: d.catIII > 0 ? '#ffeb3b' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.catIII}</Typography>
            <Typography sx={{ color: (d.open || 0) > 0 ? '#f44336' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.open || 0}</Typography>
            <Typography sx={{ color: (d.notAFinding || 0) > 0 ? '#4caf50' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.notAFinding || 0}</Typography>
            <Typography sx={{ color: (d.notApplicable || 0) > 0 ? '#00bcd4' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.notApplicable || 0}</Typography>
            <Typography sx={{ color: (d.notReviewed || 0) > 0 ? '#7a8fa6' : '#4a5a70', fontWeight: 700, fontSize: 14 }}>{d.notReviewed || 0}</Typography>
          </Box>
        ))}
      </Paper>

      <Typography sx={{ color: '#4a5a70', fontSize: 12, marginTop: 1.5 }}>
        {filtered.length} of {devices.length} devices · Sorted by CAT I findings, then OS
      </Typography>

      <StigDetailDrawer
        asset={drawerAsset}
        open={Boolean(drawerAsset)}
        onClose={() => setDrawerAsset(null)}
      />

      <BulkStigAssignDialog open={bulkAssignOpen} onClose={() => setBulkAssignOpen(false)} />
      <BulkCklUploadDialog open={bulkCklOpen} onClose={() => setBulkCklOpen(false)} />
    </Box>
  );
}
